/**
 * ShareFilePreview.tsx
 * 分享页面文件预览组件
 *
 * 功能:
 * - 图片/视频/音频预览
 * - PDF文档预览
 * - 文本/代码预览（带语法高亮）
 * - Markdown 渲染预览（带代码高亮、表格样式、架构图支持）
 * - Office文档预览（Word/Excel本地渲染，保留样式）
 * - 缩放控制和窗口尺寸切换
 * - 支持单文件分享和文件夹分享中的子文件预览
 */

import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { renderAsync } from 'docx-preview';
import * as XLSX from 'xlsx';
import * as pdfjsLib from 'pdfjs-dist';
import ePub from 'epubjs';
import JSZip from 'jszip';
import Papa from 'papaparse';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import hljs from 'highlight.js';
import {
  X,
  Download,
  FileText,
  Volume2,
  FileSpreadsheet,
  Presentation,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  RotateCcw,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Archive,
  File,
  Search,
  ArrowUpDown,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { FileIcon } from '@/components/files/FileIcon';
import { shareApi } from '@/services/api';
import { formatBytes, decodeFileName } from '@/utils';
import { cn } from '@/utils';

import 'highlight.js/styles/github-dark.css';
import 'katex/dist/katex.min.css';

// PDF.js 5.x 配置 - 使用 CDN worker
pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://unpkg.com/pdfjs-dist@5.5.207/build/pdf.worker.min.mjs';

interface PreviewInfo {
  id: string;
  name: string;
  size: number;
  mimeType: string | null;
  previewType: string;
  canPreview: boolean;
}

interface ShareFilePreviewProps {
  shareId: string;
  file: {
    id: string;
    name: string;
    size: number;
    mimeType: string | null;
  };
  password?: string;
  isChildFile?: boolean;
  onClose: () => void;
  onDownload: () => void;
}

type WindowSize = 'small' | 'medium' | 'large' | 'fullscreen';

const WINDOW_SIZE_CONFIG: Record<WindowSize, { width: string; height: string; maxWidth: string }> = {
  small: { width: '60vw', height: '70vh', maxWidth: '800px' },
  medium: { width: '80vw', height: '85vh', maxWidth: '1200px' },
  large: { width: '90vw', height: '90vh', maxWidth: '1600px' },
  fullscreen: { width: '100vw', height: '100vh', maxWidth: '100vw' },
};

const CODE_LANGUAGE_MAP: Record<string, string> = {
  js: 'javascript',
  jsx: 'javascript',
  ts: 'typescript',
  tsx: 'typescript',
  py: 'python',
  rb: 'ruby',
  go: 'go',
  rs: 'rust',
  java: 'java',
  c: 'c',
  cpp: 'cpp',
  h: 'c',
  hpp: 'cpp',
  cs: 'csharp',
  php: 'php',
  swift: 'swift',
  kt: 'kotlin',
  scala: 'scala',
  r: 'r',
  sql: 'sql',
  sh: 'bash',
  bash: 'bash',
  zsh: 'bash',
  json: 'json',
  xml: 'xml',
  yaml: 'yaml',
  yml: 'yaml',
  css: 'css',
  scss: 'scss',
  less: 'less',
  html: 'html',
  vue: 'vue',
  dockerfile: 'dockerfile',
  makefile: 'makefile',
  toml: 'toml',
  ini: 'ini',
  env: 'bash',
};

const CODE_EXTENSIONS = new Set([
  'js', 'jsx', 'ts', 'tsx', 'py', 'rb', 'go', 'rs', 'java', 'c', 'cpp', 'h', 'hpp',
  'cs', 'php', 'swift', 'kt', 'scala', 'r', 'sql', 'sh', 'bash', 'zsh', 'json',
  'xml', 'yaml', 'yml', 'css', 'scss', 'less', 'html', 'vue', 'dockerfile',
  'makefile', 'toml', 'ini', 'env'
]);

function getLanguageFromExtension(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return CODE_LANGUAGE_MAP[ext] || 'plaintext';
}

function isCodeFile(filename: string): boolean {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  return CODE_EXTENSIONS.has(ext);
}

function highlightCode(code: string, language: string): string {
  try {
    if (hljs.getLanguage(language)) {
      return hljs.highlight(code, { language }).value;
    }
    return hljs.highlightAuto(code).value;
  } catch {
    return code;
  }
}

interface ExcelCellStyle {
  font?: {
    bold?: boolean;
    italic?: boolean;
    color?: { rgb?: string; theme?: number };
    sz?: number;
    name?: string;
  };
  fill?: {
    fgColor?: { rgb?: string; theme?: number };
    bgColor?: { rgb?: string; theme?: number };
    patternType?: string;
  };
  alignment?: {
    horizontal?: string;
    vertical?: string;
    wrapText?: boolean;
  };
  border?: {
    top?: { style?: string; color?: { rgb?: string } };
    bottom?: { style?: string; color?: { rgb?: string } };
    left?: { style?: string; color?: { rgb?: string } };
    right?: { style?: string; color?: { rgb?: string } };
  };
  numFmt?: string;
}

function rgbToHex(rgb: string | undefined): string | undefined {
  if (!rgb) return undefined;
  if (rgb.startsWith('#')) return rgb;
  if (rgb.length === 6 && /^[0-9A-Fa-f]{6}$/.test(rgb)) {
    return `#${rgb}`;
  }
  if (rgb.length === 8 && rgb.startsWith('FF')) {
    return `#${rgb.slice(2)}`;
  }
  return undefined;
}

const THEME_COLORS: Record<number, string> = {
  0: '#FFFFFF',
  1: '#000000',
  2: '#E7E6E6',
  3: '#44546A',
  4: '#5B9BD5',
  5: '#ED7D31',
  6: '#A5A5A5',
  7: '#FFC000',
  8: '#4472C4',
  9: '#70AD47',
};

function getThemeColor(theme?: number): string | undefined {
  if (theme === undefined) return undefined;
  return THEME_COLORS[theme];
}

function getExcelCellStyle(cell: XLSX.CellObject, workbook: XLSX.WorkBook): React.CSSProperties {
  const styles: React.CSSProperties = {};
  if (!cell.s) return styles;

  const cellStyle = cell.s as ExcelCellStyle;

  if (cellStyle.font) {
    if (cellStyle.font.bold) styles.fontWeight = 'bold';
    if (cellStyle.font.italic) styles.fontStyle = 'italic';
    if (cellStyle.font.sz) styles.fontSize = `${cellStyle.font.sz}px`;
    if (cellStyle.font.name) styles.fontFamily = cellStyle.font.name;
    if (cellStyle.font.color?.rgb) {
      const color = rgbToHex(cellStyle.font.color.rgb);
      if (color) styles.color = color;
    } else if (cellStyle.font.color?.theme !== undefined) {
      const themeColor = getThemeColor(cellStyle.font.color.theme);
      if (themeColor) styles.color = themeColor;
    }
  }

  if (cellStyle.fill?.patternType && cellStyle.fill.patternType !== 'none') {
    if (cellStyle.fill.fgColor?.rgb) {
      const bgColor = rgbToHex(cellStyle.fill.fgColor.rgb);
      if (bgColor) {
        styles.backgroundColor = bgColor;
      }
    } else if (cellStyle.fill.fgColor?.theme !== undefined) {
      const themeColor = getThemeColor(cellStyle.fill.fgColor.theme);
      if (themeColor) {
        styles.backgroundColor = themeColor;
      }
    }
  }

  if (cellStyle.alignment) {
    if (cellStyle.alignment.horizontal) {
      styles.textAlign = cellStyle.alignment.horizontal as React.CSSProperties['textAlign'];
    }
    if (cellStyle.alignment.vertical) {
      styles.verticalAlign = cellStyle.alignment.vertical as React.CSSProperties['verticalAlign'];
    }
    if (cellStyle.alignment.wrapText) {
      styles.whiteSpace = 'pre-wrap';
      styles.wordBreak = 'break-word';
    }
  }

  return styles;
}

function formatExcelValue(cell: XLSX.CellObject): string {
  if (cell.v === undefined || cell.v === null) return '';
  if (typeof cell.v === 'number') {
    if (cell.w) return cell.w;
    return cell.v.toLocaleString();
  }
  if (cell.v instanceof Date) {
    return cell.v.toLocaleString();
  }
  return String(cell.v);
}

function renderExcelSheetWithStyles(
  worksheet: XLSX.WorkSheet,
  workbook: XLSX.WorkBook
): { html: string; merges: XLSX.Range[] } {
  const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
  const merges = worksheet['!merges'] || [];

  const rows: string[] = [];
  rows.push(
    '<table style="border-collapse: collapse; width: 100%; font-size: 13px; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;">'
  );

  for (let row = range.s.r; row <= range.e.r; row++) {
    const cells: string[] = [];
    cells.push('<tr>');

    for (let col = range.s.c; col <= range.e.c; col++) {
      const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = worksheet[cellAddress];

      let isMerged = false;
      let rowSpan = 1;
      let colSpan = 1;

      for (const merge of merges) {
        if (row >= merge.s.r && row <= merge.e.r && col >= merge.s.c && col <= merge.e.c) {
          if (row === merge.s.r && col === merge.s.c) {
            rowSpan = merge.e.r - merge.s.r + 1;
            colSpan = merge.e.c - merge.s.c + 1;
          } else {
            isMerged = true;
          }
          break;
        }
      }

      if (isMerged) {
        continue;
      }

      const baseStyle: React.CSSProperties = {
        border: '1px solid #e5e7eb',
        padding: '6px 10px',
        textAlign: 'left',
        verticalAlign: 'top',
        minWidth: '60px',
        height: '24px',
      };

      const cellStyle = cell ? getExcelCellStyle(cell, workbook) : {};
      const mergedStyle = { ...baseStyle, ...cellStyle };

      const styleStr = Object.entries(mergedStyle)
        .map(([key, value]) => {
          const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
          return `${cssKey}: ${value}`;
        })
        .join('; ');

      const value = cell ? formatExcelValue(cell) : '';
      const tag = row === range.s.r ? 'th' : 'td';
      const extraAttrs =
        rowSpan > 1 ? ` rowspan="${rowSpan}"` : '' + (colSpan > 1 ? ` colspan="${colSpan}"` : '');

      cells.push(`<${tag} style="${styleStr}"${extraAttrs}>${value || '&nbsp;'}</${tag}>`);
    }

    cells.push('</tr>');
    rows.push(cells.join(''));
  }

  rows.push('</table>');
  return { html: rows.join(''), merges };
}

export function ShareFilePreview({
  shareId,
  file,
  password,
  isChildFile = false,
  onClose,
  onDownload,
}: ShareFilePreviewProps) {
  const [textContent, setTextContent] = useState<string | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [previewInfo, setPreviewInfo] = useState<PreviewInfo | null>(null);
  const [officeLoading, setOfficeLoading] = useState(false);
  const [officeError, setOfficeError] = useState<string | null>(null);
  const [excelData, setExcelData] = useState<XLSX.WorkSheet | null>(null);
  const [excelLoading, setExcelLoading] = useState(false);
  const [excelWorkbook, setExcelWorkbook] = useState<XLSX.WorkBook | null>(null);
  const [activeSheetName, setActiveSheetName] = useState<string | null>(null);
  const [excelHtml, setExcelHtml] = useState<string | null>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const docxContainerRef = useRef<HTMLDivElement>(null);

  const [zoomLevel, setZoomLevel] = useState(100);
  const [windowSize, setWindowSize] = useState<WindowSize>('medium');

  const [csvData, setCsvData] = useState<string[][] | null>(null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<string[][]>([]);
  const [csvSortColumn, setCsvSortColumn] = useState<number | null>(null);
  const [csvSortAsc, setCsvSortAsc] = useState(true);
  const [csvSearchTerm, setCsvSearchTerm] = useState('');
  const [csvCurrentPage, setCsvCurrentPage] = useState(1);
  const [csvPageSize] = useState(50);

  const [zipContents, setZipContents] = useState<{ name: string; size: number; isDir: boolean }[]>([]);
  const [zipLoading, setZipLoading] = useState(false);
  const [zipStats, setZipStats] = useState<{ totalFiles: number; totalDirs: number; totalSize: number; compressedSize: number } | null>(null);

  const [fontPreview, setFontPreview] = useState<{ name: string; preview: string } | null>(null);
  const [fontLoading, setFontLoading] = useState(false);

  const [epubViewer, setEpubViewer] = useState<ePub.Book | null>(null);
  const [epubRendition, setEpubRendition] = useState<ePub.Rendition | null>(null);
  const [epubLoading, setEpubLoading] = useState(false);
  const [epubCurrentPage, setEpubCurrentPage] = useState(0);
  const [epubTotalPages, setEpubTotalPages] = useState(0);
  const [epubToc, setEpubToc] = useState<{ href: string; label: string }[]>([]);
  const [epubShowToc, setEpubShowToc] = useState(true);
  const [epubSpread, setEpubSpread] = useState<'none' | 'auto'>('none');
  const [epubTheme, setEpubTheme] = useState<'light' | 'dark' | 'sepia' | 'green'>('light');
  const [epubFontSize, setEpubFontSize] = useState(100);
  const [epubError, setEpubError] = useState<string | null>(null);
  const epubContainerRef = useRef<HTMLDivElement>(null);

  const [pdfLoading, setPdfLoading] = useState(false);

  const mimeType = file.mimeType;
  const isImage = mimeType?.startsWith('image/');
  const isVideo = mimeType?.startsWith('video/');
  const isAudio = mimeType?.startsWith('audio/');
  const isPdf = mimeType === 'application/pdf';
  const isMarkdown = mimeType === 'text/markdown' || file.name.endsWith('.md');
  const isPlainText = mimeType === 'text/plain' || file.name.endsWith('.txt');
  const isText =
    mimeType?.startsWith('text/') ||
    mimeType === 'application/json' ||
    mimeType === 'application/xml' ||
    mimeType === 'application/javascript' ||
    mimeType === 'application/typescript';
  const isWord =
    mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    mimeType === 'application/msword';
  const isExcel =
    mimeType === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    mimeType === 'application/vnd.ms-excel' ||
    mimeType === 'text/csv';
  const isCsv = mimeType === 'text/csv' || file.name.endsWith('.csv');
  const isPpt =
    mimeType === 'application/vnd.openxmlformats-officedocument.presentationml.presentation' ||
    mimeType === 'application/vnd.ms-powerpoint';
  const isOffice = isWord || isExcel || isPpt;
  const isCode = isText && !isMarkdown && !isPlainText && isCodeFile(file.name);
  const isEpub = mimeType === 'application/epub+zip' || file.name.endsWith('.epub');
  const isFont =
    mimeType === 'font/ttf' ||
    mimeType === 'font/otf' ||
    mimeType === 'font/woff' ||
    mimeType === 'font/woff2' ||
    file.name.endsWith('.ttf') ||
    file.name.endsWith('.otf') ||
    file.name.endsWith('.woff') ||
    file.name.endsWith('.woff2');
  const isZip =
    mimeType === 'application/zip' ||
    mimeType === 'application/x-zip-compressed' ||
    file.name.endsWith('.zip');

  const canPreview = isImage || isVideo || isAudio || isPdf || isText || isMarkdown || isOffice || isEpub || isFont || isZip || isCsv;

  const detectedLanguage = useMemo(() => {
    return getLanguageFromExtension(file.name);
  }, [file.name]);

  const getPreviewUrl = useCallback(() => {
    if (isChildFile) {
      if (isVideo || isAudio) {
        return shareApi.childStreamUrl(shareId, file.id, password);
      }
      return shareApi.childPreviewUrl(shareId, file.id, password);
    }
    if (isVideo || isAudio) {
      return shareApi.streamUrl(shareId, password);
    }
    return shareApi.previewUrl(shareId, password);
  }, [isChildFile, isVideo, isAudio, shareId, file.id, password]);

  useEffect(() => {
    setLoadError(false);
    setTextContent(null);
    setPreviewInfo(null);
    setOfficeLoading(false);
    setOfficeError(null);
    setExcelData(null);
    setExcelLoading(false);
    setExcelWorkbook(null);
    setActiveSheetName(null);
    setExcelHtml(null);
    setZoomLevel(100);
    setWindowSize('medium');
    setCsvData(null);
    setCsvLoading(false);
    setCsvHeaders([]);
    setCsvRows([]);
    setCsvSortColumn(null);
    setCsvSortAsc(true);
    setCsvSearchTerm('');
    setCsvCurrentPage(1);
    setZipContents([]);
    setZipLoading(false);
    setZipStats(null);
    setFontPreview(null);
    setFontLoading(false);
    setEpubViewer(null);
    setEpubRendition(null);
    setEpubLoading(false);
    setEpubCurrentPage(0);
    setEpubTotalPages(0);
    setEpubToc([]);
    setEpubShowToc(true);
    setEpubError(null);
    setPdfLoading(false);
  }, [shareId, file.id, password]);

  useEffect(() => {
    if ((!isText && !isMarkdown) || !canPreview) return;

    const fetchTextContent = async () => {
      try {
        const res = isChildFile
          ? await shareApi.getChildRawContent(shareId, file.id, password)
          : await shareApi.getRawContent(shareId, password);
        if (res.data.data?.content) {
          setTextContent(res.data.data.content);
        }
      } catch {
        // 如果API调用失败，尝试通过fetch获取并处理编码
        try {
          const response = await fetch(getPreviewUrl());
          // 对于CSV文件，尝试使用UTF-8编码
          const text = await response.text();
          setTextContent(text);
        } catch {
          setLoadError(true);
        }
      }
    };

    fetchTextContent();
  }, [shareId, file.id, password, isText, isMarkdown, canPreview, isChildFile, getPreviewUrl]);

  const loadDocxPreview = useCallback(async () => {
    if (!isWord || !docxContainerRef.current) return;

    setOfficeLoading(true);
    setOfficeError(null);

    try {
      const response = await fetch(getPreviewUrl());
      if (!response.ok) {
        throw new Error(`文件加载失败: ${response.status}`);
      }
      const arrayBuffer = await response.arrayBuffer();

      if (!docxContainerRef.current) {
        throw new Error('容器不可用');
      }

      if (arrayBuffer.byteLength === 0) {
        throw new Error('文件内容为空');
      }

      docxContainerRef.current.innerHTML = '';

      await renderAsync(arrayBuffer, docxContainerRef.current, undefined, {
        className: 'docx-preview-wrapper',
        inWrapper: true,
        ignoreWidth: false,
        ignoreHeight: false,
        ignoreFonts: false,
        breakPages: true,
        ignoreLastRenderedPageBreak: true,
        experimental: false,
        trimXmlDeclaration: true,
        useBase64URL: true,
        renderHeaders: true,
        renderFooters: true,
        renderFootnotes: true,
        renderEndnotes: true,
      });

      const renderedContent = docxContainerRef.current.querySelector('.docx-preview-wrapper');
      if (!renderedContent || docxContainerRef.current.children.length === 0) {
        throw new Error('文档渲染结果为空');
      }
    } catch (err) {
      console.error('DOCX preview error:', err);
      setOfficeError(err instanceof Error ? err.message : '文档预览失败，请下载查看');
    } finally {
      setOfficeLoading(false);
    }
  }, [isWord, shareId, file.id, password, isChildFile, getPreviewUrl]);

  const loadExcelPreview = useCallback(async () => {
    if (!isExcel) return;

    setExcelLoading(true);
    try {
      const response = await fetch(getPreviewUrl());
      if (!response.ok) {
        throw new Error(`文件加载失败: ${response.status}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, {
        type: 'array',
        cellStyles: true,
        cellNF: true,
        cellDates: true,
      });
      setExcelWorkbook(workbook);
      const firstSheetName = workbook.SheetNames[0];
      if (firstSheetName) {
        setActiveSheetName(firstSheetName);
        const worksheet = workbook.Sheets[firstSheetName];
        if (worksheet) {
          setExcelData(worksheet);
          const { html } = renderExcelSheetWithStyles(worksheet, workbook);
          setExcelHtml(html);
        }
      } else {
        setLoadError(true);
      }
    } catch (err) {
      console.error('Excel preview error:', err);
      setLoadError(true);
    } finally {
      setExcelLoading(false);
    }
  }, [isExcel, shareId, file.id, password, isChildFile, getPreviewUrl]);

  const handleSheetChange = useCallback(
    (sheetName: string) => {
      if (!excelWorkbook) return;
      setActiveSheetName(sheetName);
      const worksheet = excelWorkbook.Sheets[sheetName];
      if (worksheet) {
        setExcelData(worksheet);
        const { html } = renderExcelSheetWithStyles(worksheet, excelWorkbook);
        setExcelHtml(html);
      }
    },
    [excelWorkbook]
  );

  const loadCsvPreview = useCallback(async () => {
    if (!isCsv) return;

    setCsvLoading(true);
    try {
      const response = await fetch(getPreviewUrl());
      if (!response.ok) {
        throw new Error(`文件加载失败: ${response.status}`);
      }
      const text = await response.text();
      
      Papa.parse<string[]>(text, {
        complete: (results) => {
          const data = results.data;
          if (data.length > 0 && data[0]) {
            setCsvHeaders(data[0]);
            setCsvRows(data.slice(1).filter(row => row.some(cell => cell.trim() !== '')));
            setCsvData(data);
          }
          setCsvLoading(false);
        },
        error: (error: Error) => {
          console.error('CSV parse error:', error);
          setLoadError(true);
          setCsvLoading(false);
        },
      });
    } catch (err) {
      console.error('CSV preview error:', err);
      setLoadError(true);
      setCsvLoading(false);
    }
  }, [isCsv, getPreviewUrl]);

  const loadZipPreview = useCallback(async () => {
    if (!isZip) return;

    setZipLoading(true);
    try {
      const response = await fetch(getPreviewUrl());
      if (!response.ok) {
        throw new Error(`文件加载失败: ${response.status}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);
      
      const contents: { name: string; size: number; isDir: boolean }[] = [];
      let totalSize = 0;
      let compressedSize = 0;
      let totalFiles = 0;
      let totalDirs = 0;

      zip.forEach((relativePath, zipEntry) => {
        const isDir = zipEntry.dir;
        const size = zipEntry.dir ? 0 : 0;
        contents.push({
          name: relativePath,
          size: size,
          isDir: isDir,
        });
        
        if (isDir) {
          totalDirs++;
        } else {
          totalFiles++;
        }
      });

      setZipContents(contents);
      setZipStats({
        totalFiles,
        totalDirs,
        totalSize,
        compressedSize,
      });
      setZipLoading(false);
    } catch (err) {
      console.error('ZIP preview error:', err);
      setLoadError(true);
      setZipLoading(false);
    }
  }, [isZip, getPreviewUrl]);

  const loadFontPreview = useCallback(async () => {
    if (!isFont) return;

    setFontLoading(true);
    try {
      const fontName = `PreviewFont-${file.id}`;
      const fontFace = new FontFace(fontName, `url(${getPreviewUrl()})`);
      
      await fontFace.load();
      document.fonts.add(fontFace);
      
      setFontPreview({
        name: fontName,
        preview: decodeFileName(file.name),
      });
      setFontLoading(false);
    } catch (err) {
      console.error('Font preview error:', err);
      setLoadError(true);
      setFontLoading(false);
    }
  }, [isFont, getPreviewUrl, file.id, file.name]);

  const applyEpubFontSize = useCallback(() => {
    if (!epubRendition) return;

    try {
      epubRendition.themes.default({
        'body': {
          'font-size': `${epubFontSize}%`,
        },
        'p': {
          'font-size': `${epubFontSize}%`,
        },
        'h1': {
          'font-size': `${epubFontSize * 1.5}%`,
        },
        'h2': {
          'font-size': `${epubFontSize * 1.3}%`,
        },
        'h3': {
          'font-size': `${epubFontSize * 1.17}%`,
        },
        'h4': {
          'font-size': `${epubFontSize}%`,
        },
        'h5': {
          'font-size': `${epubFontSize * 0.83}%`,
        },
        'h6': {
          'font-size': `${epubFontSize * 0.67}%`,
        },
      });
    } catch (e) {
      console.error('Failed to apply EPUB font size:', e);
    }
  }, [epubRendition, epubFontSize]);

  const loadEpubPreview = useCallback(async () => {
    if (!isEpub || !epubContainerRef.current) return;

    setEpubLoading(true);
    setEpubError(null);
    try {
      const response = await fetch(getPreviewUrl());
      if (!response.ok) {
        throw new Error(`下载失败: ${response.status}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      
      const book = ePub(arrayBuffer);
      setEpubViewer(book);
      
      await book.ready;
      
      const navigation = await book.loaded.navigation;
      if (navigation && navigation.toc) {
        setEpubToc(navigation.toc.map((item: any) => ({
          href: item.href,
          label: item.label,
        })));
      }
      
      const spine = await book.loaded.spine;
      if (spine) {
        setEpubTotalPages((spine as any).length || 0);
      }
      
      if (epubContainerRef.current) {
        const rendition = book.renderTo(epubContainerRef.current, {
          width: '100%',
          height: '100%',
          spread: epubSpread,
        });
        setEpubRendition(rendition);
        
        try {
          await rendition.display();
        } catch (e) {
          setEpubError('渲染失败: ' + (e instanceof Error ? e.message : '未知错误'));
        }
        
        rendition.on('relocated', (location: any) => {
          if (location && location.start && book.spine) {
            try {
              const spineItems = (book.spine as any).items || [];
              const index = spineItems.findIndex((item: any) => item.href === location.start.href);
              if (index !== -1) {
                setEpubCurrentPage(index);
              }
            } catch (e) {
              // 忽略索引查找错误
            }
          }
        });
        
        rendition.on('error', (error: any) => {
          setEpubError('渲染错误: ' + (error instanceof Error ? error.message : '未知错误'));
        });
      }
      
      setEpubLoading(false);
    } catch (err) {
      setEpubError('EPUB 加载失败，请下载后查看');
      setEpubLoading(false);
    }
  }, [isEpub, getPreviewUrl]);

  useEffect(() => {
    if (epubRendition && epubContainerRef.current) {
      const rect = epubContainerRef.current.getBoundingClientRect();
      epubRendition.resize(rect.width, rect.height);
    }
  }, [epubShowToc, epubSpread]);

  useEffect(() => {
    if (epubRendition) {
      epubRendition.spread(epubSpread);
    }
  }, [epubSpread]);

  useEffect(() => {
    if (epubContainerRef.current) {
      const rect = epubContainerRef.current.getBoundingClientRect();
      if (rect.width > 800 && epubSpread === 'none') {
        setEpubSpread('auto');
      } else if (rect.width <= 800 && epubSpread === 'auto') {
        setEpubSpread('none');
      }
    }
  }, [isEpub]);

  useEffect(() => {
    if (epubRendition) {
      applyEpubFontSize();
    }
  }, [epubRendition, applyEpubFontSize]);

  const applyEpubTheme = useCallback(() => {
    if (!epubRendition) return;

    const themes = {
      light: {
        body: { background: '#ffffff', color: '#111111' },
        p: { color: '#111111' },
        h1: { color: '#111111' },
        h2: { color: '#111111' },
        h3: { color: '#111111' },
        h4: { color: '#111111' },
        h5: { color: '#111111' },
        h6: { color: '#111111' },
        a: { color: '#2563eb' },
      },
      dark: {
        body: { background: '#1f2937', color: '#f9fafb' },
        p: { color: '#f9fafb' },
        h1: { color: '#f9fafb' },
        h2: { color: '#f9fafb' },
        h3: { color: '#f9fafb' },
        h4: { color: '#f9fafb' },
        h5: { color: '#f9fafb' },
        h6: { color: '#f9fafb' },
        a: { color: '#60a5fa' },
      },
      sepia: {
        body: { background: '#f4ecd8', color: '#5b4636' },
        p: { color: '#5b4636' },
        h1: { color: '#5b4636' },
        h2: { color: '#5b4636' },
        h3: { color: '#5b4636' },
        h4: { color: '#5b4636' },
        h5: { color: '#5b4636' },
        h6: { color: '#5b4636' },
        a: { color: '#854d0e' },
      },
      green: {
        body: { background: '#c7edcc', color: '#2e4f2d' },
        p: { color: '#2e4f2d' },
        h1: { color: '#2e4f2d' },
        h2: { color: '#2e4f2d' },
        h3: { color: '#2e4f2d' },
        h4: { color: '#2e4f2d' },
        h5: { color: '#2e4f2d' },
        h6: { color: '#2e4f2d' },
        a: { color: '#166534' },
      },
    };

    const theme = themes[epubTheme];
    
    try {
      epubRendition.themes.default({
        'body': {
          ...theme.body,
          'font-size': `${epubFontSize}%`,
        },
        'p': {
          ...theme.p,
          'font-size': `${epubFontSize}%`,
        },
        'h1': {
          ...theme.h1,
          'font-size': `${epubFontSize * 1.5}%`,
        },
        'h2': {
          ...theme.h2,
          'font-size': `${epubFontSize * 1.3}%`,
        },
        'h3': {
          ...theme.h3,
          'font-size': `${epubFontSize * 1.17}%`,
        },
        'h4': {
          ...theme.h4,
          'font-size': `${epubFontSize}%`,
        },
        'h5': {
          ...theme.h5,
          'font-size': `${epubFontSize * 0.83}%`,
        },
        'h6': {
          ...theme.h6,
          'font-size': `${epubFontSize * 0.67}%`,
        },
        'a': theme.a,
      });
    } catch (e) {
      console.error('Failed to apply EPUB theme:', e);
    }
  }, [epubRendition, epubTheme, epubFontSize]);

  useEffect(() => {
    if (epubRendition) {
      applyEpubTheme();
    }
  }, [epubRendition, applyEpubTheme]);

  useEffect(() => {
    const checkDarkMode = () => {
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        setEpubTheme('dark');
      }
    };
    
    checkDarkMode();
    
    const darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleDarkModeChange = (e: MediaQueryListEvent) => {
      if (e.matches && epubTheme === 'light') {
        setEpubTheme('dark');
      } else if (!e.matches && epubTheme === 'dark') {
        setEpubTheme('light');
      }
    };
    
    darkModeMediaQuery.addEventListener('change', handleDarkModeChange);
    return () => darkModeMediaQuery.removeEventListener('change', handleDarkModeChange);
  }, [epubTheme]);

  const handleZoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 25, 200));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 25, 50));
  }, []);

  const handleZoomReset = useCallback(() => {
    setZoomLevel(100);
  }, []);

  const handleToggleFullscreen = useCallback(() => {
    setWindowSize((prev) => (prev === 'fullscreen' ? 'medium' : 'fullscreen'));
  }, []);

  const cycleWindowSize = useCallback(() => {
    setWindowSize((prev) => {
      const sizes: WindowSize[] = ['small', 'medium', 'large', 'fullscreen'];
      const currentIndex = sizes.indexOf(prev);
      const nextIndex = (currentIndex + 1) % sizes.length;
      return sizes[nextIndex] as WindowSize;
    });
  }, []);

  useEffect(() => {
    if (isWord) {
      loadDocxPreview();
    }
  }, [isWord, loadDocxPreview]);

  useEffect(() => {
    if (isExcel) {
      loadExcelPreview();
    }
  }, [isExcel, loadExcelPreview]);

  useEffect(() => {
    if (isCsv) {
      loadCsvPreview();
    }
  }, [isCsv, loadCsvPreview]);

  useEffect(() => {
    if (isZip) {
      loadZipPreview();
    }
  }, [isZip, loadZipPreview]);

  useEffect(() => {
    if (isFont) {
      loadFontPreview();
    }
  }, [isFont, loadFontPreview]);

  useEffect(() => {
    if (isEpub) {
      loadEpubPreview();
    }
  }, [isEpub, loadEpubPreview]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const getOfficeIcon = () => {
    if (!mimeType) return <FileText className="h-6 w-6" />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <FileText className="h-6 w-6" />;
    if (mimeType.includes('excel') || mimeType.includes('sheet')) return <FileSpreadsheet className="h-6 w-6" />;
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation'))
      return <Presentation className="h-6 w-6" />;
    return <FileText className="h-6 w-6" />;
  };

  const getOfficeTypeName = () => {
    if (isWord) return 'Word 文档';
    if (isExcel) return 'Excel 表格';
    if (isPpt) return 'PowerPoint 演示文稿';
    return 'Office 文档';
  };

  const renderOfficeFallback = (message?: string) => (
    <div className="flex items-center justify-center h-full">
      <div className="text-center py-12 px-6 space-y-4">
        <div className="w-16 h-16 mx-auto rounded-xl bg-primary/10 flex items-center justify-center">
          {getOfficeIcon()}
        </div>
        <div>
          <p className="font-medium">{decodeFileName(file.name)}</p>
          <p className="text-sm text-muted-foreground mt-1">{getOfficeTypeName()}</p>
          <p className="text-xs text-muted-foreground mt-2">{message || '暂不支持在线预览，请下载查看'}</p>
        </div>
        <Button onClick={onDownload}>
          <Download className="h-4 w-4 mr-2" />
          下载文件
        </Button>
      </div>
    </div>
  );

  const sizeConfig = WINDOW_SIZE_CONFIG[windowSize];
  const showZoomControls = isText || isMarkdown || isExcel || isWord;
  const showSheetTabs = isExcel && excelWorkbook && excelWorkbook.SheetNames.length > 1;

  const highlightedCode = useMemo(() => {
    if (!textContent || !isCode) return null;
    return highlightCode(textContent, detectedLanguage);
  }, [textContent, isCode, detectedLanguage]);

  return (
    <div
      ref={overlayRef}
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm',
        windowSize === 'fullscreen' ? 'p-0' : 'p-4'
      )}
      onClick={(e) => e.target === overlayRef.current && onClose()}
    >
      <div
        className={cn(
          'relative flex flex-col bg-card border rounded-xl shadow-2xl overflow-hidden transition-all duration-300',
          windowSize === 'fullscreen' ? 'rounded-none' : ''
        )}
        style={{
          width: sizeConfig.width,
          height: sizeConfig.height,
          maxWidth: sizeConfig.maxWidth,
        }}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b flex-shrink-0">
          <FileIcon mimeType={mimeType} isFolder={false} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="font-medium truncate text-sm">{decodeFileName(file.name)}</p>
            <p className="text-xs text-muted-foreground">
              {formatBytes(file.size)}
              {previewInfo?.previewType && previewInfo.previewType !== 'unknown' && (
                <span className="ml-2 opacity-60">({previewInfo.previewType})</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {showZoomControls && (
              <div className="flex items-center gap-0.5 mr-2 px-2 py-1 bg-muted/50 rounded-md">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  title="缩小"
                  onClick={handleZoomOut}
                  disabled={zoomLevel <= 50}
                >
                  <ZoomOut className="h-3.5 w-3.5" />
                </Button>
                <span className="text-xs min-w-[40px] text-center">{zoomLevel}%</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6"
                  title="放大"
                  onClick={handleZoomIn}
                  disabled={zoomLevel >= 200}
                >
                  <ZoomIn className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6" title="重置缩放" onClick={handleZoomReset}>
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              title={windowSize === 'fullscreen' ? '退出全屏' : '全屏'}
              onClick={handleToggleFullscreen}
            >
              {windowSize === 'fullscreen' ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" title="切换窗口大小" onClick={cycleWindowSize}>
              <span className="text-xs font-medium">
                {windowSize === 'small' ? 'S' : windowSize === 'medium' ? 'M' : windowSize === 'large' ? 'L' : 'F'}
              </span>
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" title="下载" onClick={onDownload}>
              <Download className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" title="关闭" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {showSheetTabs && (
          <div className="flex items-center gap-1 px-4 py-2 border-b bg-muted/30 overflow-x-auto flex-shrink-0">
            {excelWorkbook.SheetNames.map((name) => (
              <button
                key={name}
                onClick={() => handleSheetChange(name)}
                className={cn(
                  'px-3 py-1.5 text-xs rounded-md whitespace-nowrap transition-colors',
                  activeSheetName === name
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground'
                )}
              >
                {name}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-auto min-h-0">
          {loadError ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center py-12 text-muted-foreground px-6">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>预览加载失败</p>
              </div>
            </div>
          ) : !canPreview ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center py-12 px-6 space-y-4">
                <FileIcon mimeType={mimeType} size="lg" className="mx-auto" />
                <div>
                  <p className="font-medium">{decodeFileName(file.name)}</p>
                  <p className="text-sm text-muted-foreground mt-1">{formatBytes(file.size)}</p>
                  <p className="text-sm text-muted-foreground">{mimeType || '未知类型'}</p>
                </div>
                <Button onClick={onDownload}>
                  <Download className="h-4 w-4 mr-2" />
                  下载文件
                </Button>
              </div>
            </div>
          ) : isImage ? (
            <div className="flex items-center justify-center h-full overflow-auto p-4">
              <img
                src={getPreviewUrl()}
                alt={decodeFileName(file.name)}
                className="max-w-full max-h-full object-contain"
                style={{ transform: `scale(${zoomLevel / 100})` }}
                onError={() => setLoadError(true)}
              />
            </div>
          ) : isVideo ? (
            <div className="flex items-center justify-center h-full">
              <video
                src={getPreviewUrl()}
                controls
                className="max-w-full max-h-full"
                onError={() => setLoadError(true)}
              />
            </div>
          ) : isAudio ? (
            <div className="flex items-center justify-center h-full">
              <div className="p-8 w-full max-w-md space-y-4">
                <div className="flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <Volume2 className="h-10 w-10 text-primary" />
                  </div>
                </div>
                <p className="text-center font-medium">{decodeFileName(file.name)}</p>
                <audio src={getPreviewUrl()} controls className="w-full" onError={() => setLoadError(true)} />
              </div>
            </div>
          ) : isPdf ? (
            <iframe
              src={getPreviewUrl()}
              className="w-full h-full border-0"
              title={decodeFileName(file.name)}
              onError={() => setLoadError(true)}
            />
          ) : isEpub ? (
            <div className="w-full h-full flex flex-col relative overflow-hidden">
              {epubLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 z-10">
                  <div className="text-muted-foreground text-sm">正在加载电子书...</div>
                </div>
              )}
              {epubError && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 z-10">
                  <div className="text-center">
                    <BookOpen className="h-12 w-12 mx-auto mb-2 text-muted-foreground opacity-50" />
                    <p className="text-muted-foreground">{epubError}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center justify-between gap-2 p-2 border-b bg-muted/30">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEpubShowToc(!epubShowToc)}
                  >
                    <BookOpen className="h-4 w-4 mr-1" />
                    目录
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEpubSpread(epubSpread === 'none' ? 'auto' : 'none')}
                  >
                    {epubSpread === 'none' ? (
                      <><BookOpen className="h-4 w-4 mr-1" />双页</>
                    ) : (
                      <><FileText className="h-4 w-4 mr-1" />单页</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const themes: Array<'light' | 'dark' | 'sepia' | 'green'> = ['light', 'dark', 'sepia', 'green'];
                      const currentIndex = themes.indexOf(epubTheme);
                      const nextIndex = (currentIndex + 1) % themes.length;
                      const nextTheme = themes[nextIndex];
                      if (nextTheme) {
                        setEpubTheme(nextTheme);
                      }
                    }}
                  >
                    {epubTheme === 'light' && '☀️ 浅色'}
                    {epubTheme === 'dark' && '🌙 深色'}
                    {epubTheme === 'sepia' && '📜 棕黄'}
                    {epubTheme === 'green' && '🌿 豆绿'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEpubFontSize(Math.max(70, epubFontSize - 10));
                    }}
                    disabled={epubFontSize <= 70}
                  >
                    A-
                  </Button>
                  <span className="text-sm">{epubFontSize}%</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEpubFontSize(Math.min(150, epubFontSize + 10));
                    }}
                    disabled={epubFontSize >= 150}
                  >
                    A+
                  </Button>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (epubRendition) {
                        epubRendition.prev();
                      }
                    }}
                    disabled={epubCurrentPage <= 0}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <span className="text-sm">
                    {epubTotalPages > 0 ? `${epubCurrentPage + 1} / ${epubTotalPages}` : '加载中...'}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (epubRendition) {
                        epubRendition.next();
                      }
                    }}
                    disabled={epubCurrentPage >= epubTotalPages - 1}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="flex-1 flex">
                {epubShowToc && epubToc.length > 0 && (
                  <div className="w-64 border-r overflow-auto p-4 bg-muted/20">
                    <h3 className="font-semibold mb-2 text-sm">目录</h3>
                    <ul className="space-y-1">
                      {epubToc.map((item, index) => (
                        <li key={index}>
                          <button
                            className="text-sm text-left w-full px-2 py-1 rounded hover:bg-muted/50 transition-colors"
                            onClick={() => {
                              if (epubRendition) {
                                epubRendition.display(item.href);
                              }
                            }}
                          >
                            {item.label}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <div ref={epubContainerRef} className="flex-1 overflow-auto" />
              </div>
            </div>
          ) : isFont ? (
            <div className="w-full h-full flex flex-col items-center justify-center p-8 bg-white dark:bg-gray-900">
              {fontLoading ? (
                <div className="text-muted-foreground text-sm">正在加载字体...</div>
              ) : fontPreview ? (
                <div className="text-center space-y-8 w-full">
                  <div className="space-y-4">
                    <h2 className="text-2xl font-bold" style={{ fontFamily: fontPreview.name }}>
                      字体预览
                    </h2>
                    <p className="text-lg" style={{ fontFamily: fontPreview.name }}>
                      {fontPreview.preview}
                    </p>
                  </div>
                  <div className="space-y-6 text-left max-w-4xl mx-auto">
                    <div style={{ fontFamily: fontPreview.name }}>
                      <h3 className="text-xl font-semibold mb-2">大写字母</h3>
                      <p className="text-3xl">ABCDEFGHIJKLMNOPQRSTUVWXYZ</p>
                    </div>
                    <div style={{ fontFamily: fontPreview.name }}>
                      <h3 className="text-xl font-semibold mb-2">小写字母</h3>
                      <p className="text-3xl">abcdefghijklmnopqrstuvwxyz</p>
                    </div>
                    <div style={{ fontFamily: fontPreview.name }}>
                      <h3 className="text-xl font-semibold mb-2">数字</h3>
                      <p className="text-3xl">0123456789</p>
                    </div>
                    <div style={{ fontFamily: fontPreview.name }}>
                      <h3 className="text-xl font-semibold mb-2">中文示例</h3>
                      <p className="text-2xl">天地玄黄，宇宙洪荒。日月盈昃，辰宿列张。</p>
                    </div>
                    <div style={{ fontFamily: fontPreview.name }}>
                      <h3 className="text-xl font-semibold mb-2">英文示例</h3>
                      <p className="text-lg leading-relaxed">
                        The quick brown fox jumps over the lazy dog. Pack my box with five dozen liquor jugs.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-muted-foreground text-sm">字体加载失败</div>
              )}
            </div>
          ) : isZip ? (
            <div className="w-full h-full flex flex-col bg-white dark:bg-gray-900">
              {zipLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-muted-foreground text-sm">正在加载压缩包...</div>
                </div>
              ) : (
                <>
                  {zipStats && (
                    <div className="p-4 border-b bg-muted/30">
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <File className="h-4 w-4" />
                          {zipStats.totalFiles} 个文件
                        </span>
                        <span className="flex items-center gap-1">
                          <Archive className="h-4 w-4" />
                          {zipStats.totalDirs} 个文件夹
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="flex-1 overflow-auto p-4">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium">文件名</th>
                          <th className="px-4 py-2 text-right font-medium">类型</th>
                        </tr>
                      </thead>
                      <tbody>
                        {zipContents.map((item, index) => (
                          <tr key={index} className="border-b hover:bg-muted/30 transition-colors">
                            <td className="px-4 py-2 font-mono text-xs truncate max-w-md">
                              {item.isDir ? (
                                <span className="flex items-center gap-1">
                                  <Archive className="h-4 w-4 text-amber-500" />
                                  {item.name}
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <File className="h-4 w-4 text-blue-500" />
                                  {item.name}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right text-muted-foreground">
                              {item.isDir ? '文件夹' : '文件'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          ) : isCsv ? (
            <div className="w-full h-full flex flex-col bg-white dark:bg-gray-900">
              {csvLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-muted-foreground text-sm">正在加载表格...</div>
                </div>
              ) : (
                <>
                  <div className="p-4 border-b bg-muted/30 space-y-3">
                    <div className="flex items-center gap-2">
                      <Search className="h-4 w-4 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="搜索..."
                        value={csvSearchTerm}
                        onChange={(e) => {
                          setCsvSearchTerm(e.target.value);
                          setCsvCurrentPage(1);
                        }}
                        className="flex-1 px-3 py-1.5 text-sm border rounded-md bg-background"
                      />
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>
                        共 {csvRows.filter(row => 
                          csvSearchTerm === '' || 
                          row.some(cell => cell.toLowerCase().includes(csvSearchTerm.toLowerCase()))
                        ).length} 条记录
                      </span>
                      <span>
                        第 {csvCurrentPage} 页，每页 {csvPageSize} 条
                      </span>
                    </div>
                  </div>
                  <div className="flex-1 overflow-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          {csvHeaders.map((header, index) => (
                            <th
                              key={index}
                              className="px-4 py-2 text-left font-medium cursor-pointer hover:bg-muted/80 transition-colors"
                              onClick={() => {
                                if (csvSortColumn === index) {
                                  setCsvSortAsc(!csvSortAsc);
                                } else {
                                  setCsvSortColumn(index);
                                  setCsvSortAsc(true);
                                }
                              }}
                            >
                              <div className="flex items-center gap-1">
                                {header}
                                <ArrowUpDown className="h-3 w-3" />
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvRows
                          .filter(row => 
                            csvSearchTerm === '' || 
                            row.some(cell => cell.toLowerCase().includes(csvSearchTerm.toLowerCase()))
                          )
                          .sort((a, b) => {
                            if (csvSortColumn === null) return 0;
                            const aVal = a[csvSortColumn] || '';
                            const bVal = b[csvSortColumn] || '';
                            return csvSortAsc ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
                          })
                          .slice((csvCurrentPage - 1) * csvPageSize, csvCurrentPage * csvPageSize)
                          .map((row, rowIndex) => (
                            <tr key={rowIndex} className="border-b hover:bg-muted/30 transition-colors">
                              {row.map((cell, cellIndex) => (
                                <td key={cellIndex} className="px-4 py-2">
                                  {cell}
                                </td>
                              ))}
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="p-4 border-t bg-muted/30 flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCsvCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={csvCurrentPage <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      第 {csvCurrentPage} 页
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCsvCurrentPage((prev) => prev + 1)}
                      disabled={csvCurrentPage * csvPageSize >= csvRows.filter(row => 
                        csvSearchTerm === '' || 
                        row.some(cell => cell.toLowerCase().includes(csvSearchTerm.toLowerCase()))
                      ).length}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : isMarkdown ? (
            <div
              className="w-full h-full overflow-auto p-6 prose dark:prose-invert max-w-none prose-table:border-collapse prose-th:border prose-th:border-border prose-th:bg-muted prose-th:p-2 prose-td:border prose-td:border-border prose-td:p-2 prose-tr:even:bg-muted/30"
              style={{ fontSize: `${zoomLevel}%` }}
            >
              {textContent !== null ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkMath]}
                  rehypePlugins={[rehypeHighlight, rehypeKatex]}
                  components={{
                    pre: ({ children, ...props }) => {
                      return (
                        <pre
                          {...props}
                          className="overflow-x-auto bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                        >
                          {children}
                        </pre>
                      );
                    },
                    code: ({ className, children, ...props }) => {
                      const match = /language-(\w+)/.exec(className || '');
                      const isInline = !match && !className?.includes('hljs');
                      if (isInline) {
                        return (
                          <code
                            className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 text-sm font-mono"
                            {...props}
                          >
                            {children}
                          </code>
                        );
                      }
                      return (
                        <code className={`${className || ''} text-gray-800 dark:text-gray-200`} {...props}>
                          {children}
                        </code>
                      );
                    },
                  }}
                >
                  {textContent}
                </ReactMarkdown>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-center text-muted-foreground text-sm py-8">加载中...</p>
                </div>
              )}
            </div>
          ) : isCode ? (
            <div
              className="w-full h-full overflow-auto bg-gray-50 dark:bg-gray-900"
              style={{ fontSize: `${zoomLevel}%` }}
            >
              {textContent !== null ? (
                <pre className="p-4 m-0 leading-relaxed">
                  <code
                    className={`language-${detectedLanguage} hljs`}
                    dangerouslySetInnerHTML={{ __html: highlightedCode || textContent }}
                  />
                </pre>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-center text-muted-foreground text-sm py-8">加载中...</p>
                </div>
              )}
            </div>
          ) : isText ? (
            <div className="w-full h-full overflow-auto bg-white dark:bg-gray-900 p-4" style={{ fontSize: `${zoomLevel}%` }}>
              {textContent !== null ? (
                <pre className="text-sm font-mono whitespace-pre-wrap leading-relaxed text-gray-800 dark:text-gray-200">
                  {textContent}
                </pre>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-center text-muted-foreground text-sm py-8">加载中...</p>
                </div>
              )}
            </div>
          ) : isOffice ? (
            <div className="w-full h-full flex flex-col relative">
              {isWord ? (
                <>
                  {officeLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 z-10">
                      <div className="text-muted-foreground text-sm">正在渲染文档...</div>
                    </div>
                  )}
                  {officeError && (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      {renderOfficeFallback(officeError)}
                    </div>
                  )}
                  <div
                    ref={docxContainerRef}
                    className={cn(
                      'w-full h-full overflow-auto bg-white dark:bg-gray-900',
                      officeLoading || officeError ? 'opacity-0' : 'opacity-100'
                    )}
                    style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top left' }}
                  />
                </>
              ) : isExcel ? (
                <>
                  {excelLoading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-900/80 z-10">
                      <div className="text-muted-foreground text-sm">正在加载表格...</div>
                    </div>
                  )}
                  {loadError ? (
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      {renderOfficeFallback('Excel 加载失败')}
                    </div>
                  ) : excelHtml ? (
                    <div
                      className="w-full h-full overflow-auto bg-white dark:bg-gray-900 p-4"
                      style={{ transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top left' }}
                      dangerouslySetInnerHTML={{ __html: excelHtml }}
                    />
                  ) : null}
                </>
              ) : isPpt ? (
                <iframe
                  src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(getPreviewUrl())}`}
                  className="w-full h-full border-0"
                  title={decodeFileName(file.name)}
                  onError={() => setLoadError(true)}
                />
              ) : (
                renderOfficeFallback()
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
